import { type AiTagId } from '@solinkcorp/api-types';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import { type Immutable } from 'immer';
import React, { useMemo } from 'react';
import { useControllerState } from 'solox';
import { IGNORE_THRESHOLD_WARNING } from '../../constants';
import { useHorizonStartMs } from '../../hooks/useHorizonStartMs';
import { useResolvedThreshold } from '../../hooks/useResolvedThreshold';
import useVehicleJourneyZones from '../../hooks/useVehicleJourneyZones';
import { type SDMJourneyWidget } from '../../layout/types';
import { averageJourneyTimeSecondsSince } from '../../ServiceDisplayEventsController';
import { type CommonWidgetProps } from '../../types';
import BigValue from '../BigValue';
import { formatJourneyDuration } from './formatJourneyDuration';
import { getMissingJourneyZones } from './getMissingJourneyZones';
import { JourneyZoneError } from './JourneyZoneError';
import WidgetCard, { type WidgetCardProps } from './WidgetCard';

interface JourneyWidgetProps extends CommonWidgetProps {
  config: Immutable<SDMJourneyWidget>;
  tagIds: AiTagId[];
}

/**
 * Average total journey time — how long a vehicle takes to get from the start zone to the
 * end zone. Unlike `TimeWidget` there is no realtime mode: a journey only exists once the
 * vehicle has left the end zone, so every horizon is window-based.
 */
const JourneyWidget: React.FC<JourneyWidgetProps> = (props) => {
  const {
    config,
    tagIds,
    eventsController,
    editing,
    hasConnectedTags,
    hasMultipleAssignments,
    hasLine,
    widget,
  } = props;

  const horizonStartMs = useHorizonStartMs(config.horizon, widget);

  // A journey needs both a start and an end zone before the edge can pair one. Surfaced on
  // the card (not in the config modal) and suppressed while editing. Keyed by siteId, so
  // switching location re-evaluates it.
  const [query] = useQueryParams();
  const journeyZones = useVehicleJourneyZones(query.get('siteId') ?? undefined);
  // Suppressed while editing: the layout editor already shows placeholder values, so an
  // error there would be noise. Also held back until the config has loaded, so a slow fetch
  // doesn't flash an error on a correctly configured site.
  const missingZones =
    editing || !journeyZones.isLoaded
      ? null
      : getMissingJourneyZones(journeyZones.hasStartZone, journeyZones.hasEndZone);

  // The journey value derives purely from `rawEvents` (that's where `vehicle_journey`
  // lands), so unlike `TimeWidget` — which also reads the summary map for its realtime
  // branch — subscribing to `rawEvents` alone is both sufficient and complete.
  const rawEvents = useControllerState(eventsController.state, (state) => state.rawEvents);

  const seconds = useMemo<number>(() => {
    if (horizonStartMs == null) return 0;
    return averageJourneyTimeSecondsSince(rawEvents, tagIds, horizonStartMs);
  }, [tagIds, horizonStartMs, rawEvents]);

  const { value: threshold, isFallback } = useResolvedThreshold(widget, config.threshold, 'TIME');
  const thresholdStr = formatJourneyDuration(threshold);
  const thresholdDebugLabel = isFallback
    ? `fallback: ${thresholdStr}`
    : `override: ${thresholdStr}`;

  let level: WidgetCardProps['level'] = 'normal';
  if (missingZones || hasMultipleAssignments || hasLine || threshold === IGNORE_THRESHOLD_WARNING) {
    level = 'normal';
  } else if (threshold > 0 && seconds >= threshold) {
    level = 'critical';
  } else if (threshold > 0 && seconds >= threshold * 0.8) {
    level = 'warning';
  }

  return (
    <WidgetCard
      level={level}
      {...props}
      title={widget.title}
      thresholdDebugLabel={thresholdDebugLabel}
      horizonStartMs={horizonStartMs}
      titleError={!!missingZones}
      largeValue
    >
      {missingZones ? (
        // The start/end zone pairing is incomplete, so there is no journey to report — explain that
        // in place of the value rather than showing a misleading zero.
        <JourneyZoneError missing={missingZones} />
      ) : (
        <BigValue scaleMultiplier={2}>
          {editing || !hasConnectedTags || hasMultipleAssignments || hasLine ? (
            <span className="p-0.5">---</span>
          ) : (
            <span className="p-0.5">{formatJourneyDuration(seconds)}</span>
          )}
        </BigValue>
      )}
    </WidgetCard>
  );
};

export default React.memo(JourneyWidget);
